import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import dayjs from 'dayjs';
import { Ellipsis } from '@/components/ui/ellipsis';
import { getSourceIconUrl } from '@/utils/source-icon';
import {
  getGeographyFlag,
  getGeographyLabel,
  compareGeographyCodesForFilter,
} from '@/shared/geography';
import { CountrySelectList } from '@/components/ui/country-select';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useAdminDeleteReferenceService,
  useAdminDeleteUpdateService,
  useAdminReferencesService,
  useAdminSourcesService,
  useAdminUpdatesService,
} from '@/services/backoffice';
import { type AdminReferenceItem, type AdminUpdateItem } from '@ditto/shared';
import { DocumentTypeBadge } from '@/pages/RegulatoryWatch/components/columns';
import {
  UPDATE_TYPE_LABELS,
  UPDATE_TYPE_OPTIONS,
} from '@/pages/RegulatoryWatch/labels';
import {
  Ban,
  Copy,
  MoreHorizontal,
  Pencil,
  Search,
  Star,
  Trash2,
} from 'lucide-react';
import {
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  type FC,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useUrlParam,
  useUrlParamCsv,
  useUrlParamEnum,
} from '@/hooks/useUrlParam';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import { CreateReferenceModal } from './CreateReferenceModal';
import { CreateUpdateModal } from './CreateUpdateModal';
import { SourcesTab } from './SourcesTab';
import { DataTable, usePagination } from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { type ColumnDef } from '@tanstack/react-table';
import {
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

// ── Shared OptionFilter (same as main app) ──

const OptionFilter: FC<{
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const isActive = selected.length > 0;
  const activeLabel =
    selected.length === 1
      ? options.find(o => o.value === selected[0])?.label
      : `${selected.length} selected`;

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={isActive ? activeLabel : undefined}
      onClear={isActive ? () => onChange([]) : undefined}
    >
      <FilterOptionList
        options={options}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};

const REFERENCE_TYPE_TABS = [
  { value: 'regulation', label: 'Regulations' },
  { value: 'guidance', label: 'Guidances' },
  { value: 'standard', label: 'Standards' },
] as const;

const SUBTAB_VALUES = ['sources', 'references', 'updates'] as const;
type Subtab = (typeof SUBTAB_VALUES)[number];

export const RegulatoryWatchAdminPage: FC = () => {
  const [subtab, setSubtab] = useUrlParamEnum<Subtab>(
    'subtab',
    SUBTAB_VALUES,
    'sources'
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={subtab === 'sources' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSubtab('sources')}
        >
          Sources
        </Button>
        <Button
          variant={subtab === 'references' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSubtab('references')}
        >
          References
        </Button>
        <Button
          variant={subtab === 'updates' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSubtab('updates')}
        >
          Updates
        </Button>
      </div>

      {subtab === 'sources' && <SourcesTab />}
      {subtab === 'references' && <ReferencesSubtab />}
      {subtab === 'updates' && <UpdatesSubtab />}
    </div>
  );
};

// ── Badge components (matching the main app's updates table) ──

const badgeClassName =
  'inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-[8px] text-[12px] font-medium text-[#171717] tracking-[0.18px] bg-[#FAFAFA] border border-solid border-[#E5E5E5]';

const SourceBadge: FC<{
  sourceTitle: string;
  sourceIcon?: string | null;
  sourceGeography?: string | null;
}> = ({ sourceTitle, sourceIcon, sourceGeography }) => {
  const displayTitle = sourceTitle;
  const flagImage = sourceGeography ? getGeographyFlag(sourceGeography) : null;

  return (
    <span className={badgeClassName}>
      {flagImage && (
        <img
          src={flagImage}
          alt=""
          className="w-[16px] h-[16px] object-contain"
        />
      )}
      {sourceIcon && !flagImage && (
        <img
          src={sourceIcon}
          alt=""
          className="w-[16px] h-[16px] object-contain"
        />
      )}
      <Ellipsis lineClamp={1} hasTooltip>
        {displayTitle}
      </Ellipsis>
    </span>
  );
};

const UpdateTypeBadge: FC<{ updateType: string }> = ({ updateType }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;

  switch (updateType) {
    case 'new':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-green-100 text-neutral-900 shadow-none">
          <Star className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'revision':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-yellow-100 text-neutral-900 shadow-none">
          <Copy className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'withdrawal':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-red-100 text-neutral-900 shadow-none">
          <Ban className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'status_change':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-blue-100 text-neutral-900 shadow-none">
          <span className="font-bold">&raquo;</span>
          {label}
        </Badge>
      );
    default:
      return (
        <Badge color="purple" className="gap-1">
          {label}
        </Badge>
      );
  }
};

// ── Reference Type Table (reusable per tab) ──

const SCROLL_SESSION_KEY_PREFIX = 'ditto:admin-updates-scroll:';

const SourceTypeTable: FC<{
  references: AdminReferenceItem[];
  sources: { id: string; title: string; icon: string | null }[];
  sourceType: string;
  isLoading: boolean;
  isFetching: boolean;
  onEdit: (ref: AdminReferenceItem) => void;
  onDelete: (ref: AdminReferenceItem) => void;
  isDeleting: boolean;
}> = ({
  references,
  sources,
  sourceType,
  isLoading,
  isFetching,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  const [search, setSearch] = useUrlParam('search');
  const [selectedSources, setSelectedSources] = useUrlParamCsv('source');
  const [selectedGeographies, setSelectedGeographies] =
    useUrlParamCsv('geography');
  const [lastUpdateFrom, setLastUpdateFrom] = useUrlParam('from');
  const [lastUpdateTo, setLastUpdateTo] = useUrlParam('to');

  // Pagination state from URL
  const [pageIndexStr, setPageIndexStr] = useUrlParam('page', '0');
  const [pageSizeStr, setPageSizeStr] = useUrlParam('pageSize', '25');
  const pageIndex = parseInt(pageIndexStr) || 0;
  const pageSize = parseInt(pageSizeStr) || 25;

  const { pagination } = usePagination({
    initialSize: pageSize,
    initialIndex: pageIndex,
    manualPagination: false,
  });

  // Update URL when pagination changes
  useMemo(() => {
    const currentPageIndex = pagination.state.pageIndex.toString();
    const currentPageSize = pagination.state.pageSize.toString();
    if (currentPageIndex !== pageIndexStr) {
      setPageIndexStr(currentPageIndex === '0' ? '' : currentPageIndex);
    }
    if (currentPageSize !== pageSizeStr && currentPageSize !== '25') {
      setPageSizeStr(currentPageSize);
    }
  }, [
    pagination.state.pageIndex,
    pagination.state.pageSize,
    pageIndexStr,
    pageSizeStr,
    setPageIndexStr,
    setPageSizeStr,
  ]);

  // Ref for scroll container
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const restoredRef = useRef(false);

  // Scroll position restoration
  const scrollSessionKey = `${SCROLL_SESSION_KEY_PREFIX}refs-${sourceType}`;

  // Restore scroll position on mount
  useLayoutEffect(() => {
    if (!scrollContainerRef.current || restoredRef.current || isLoading) {
      return;
    }
    try {
      const savedScroll = sessionStorage.getItem(scrollSessionKey);
      if (savedScroll) {
        const scrollY = parseInt(savedScroll);
        if (!isNaN(scrollY) && scrollY > 0) {
          scrollContainerRef.current.scrollTop = scrollY;
        }
      }
    } catch {
      // Ignore storage errors
    }
    restoredRef.current = true;
  }, [scrollSessionKey, isLoading]);

  // Save scroll position
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let rafId = 0;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        try {
          sessionStorage.setItem(scrollSessionKey, el.scrollTop.toString());
        } catch {
          // Ignore storage errors
        }
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', onScroll);
    };
  }, [scrollSessionKey]);

  const availableGeographies = useMemo(
    () =>
      [
        ...new Set(references.map(r => r.source.geography).filter(Boolean)),
      ].sort(compareGeographyCodesForFilter),
    [references]
  );

  const filteredReferences = useMemo(() => {
    let result = references;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        r =>
          r.title.toLowerCase().includes(q) ||
          r.externalId.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q))
      );
    }
    if (selectedSources.length > 0) {
      result = result.filter(r => selectedSources.includes(r.source.id));
    }
    if (selectedGeographies.length > 0) {
      result = result.filter(r =>
        selectedGeographies.includes(r.source.geography)
      );
    }
    if (lastUpdateFrom) {
      result = result.filter(
        r =>
          r.lastUpdatePublicationDate &&
          r.lastUpdatePublicationDate >= lastUpdateFrom
      );
    }
    if (lastUpdateTo) {
      result = result.filter(
        r =>
          r.lastUpdatePublicationDate &&
          r.lastUpdatePublicationDate <= lastUpdateTo + 'T23:59:59'
      );
    }
    return result;
  }, [
    references,
    search,
    selectedSources,
    selectedGeographies,
    lastUpdateFrom,
    lastUpdateTo,
  ]);

  const isStandard = sourceType === 'standard';

  const columns: ColumnDef<AdminReferenceItem>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Title
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <div className="max-w-[500px]">
            <Ellipsis lineClamp={1} hasTooltip>
              {row.original.title}
            </Ellipsis>
          </div>
        ),
      },
      {
        accessorKey: 'type',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Type
          </TanstackSortableColumnHeader>
        ),
        size: 130,
        cell: ({ row }) => {
          const type = row.original.type;
          if (!type) return <span className="text-muted-foreground">-</span>;
          return <DocumentTypeBadge type={type} />;
        },
      },
      ...(!isStandard
        ? [
            {
              accessorKey: 'source',
              header: 'Source',
              cell: ({ row }: { row: { original: AdminReferenceItem } }) => (
                <SourceBadge
                  sourceTitle={row.original.source.title}
                  sourceIcon={getSourceIconUrl(
                    row.original.source.id,
                    row.original.source.icon
                  )}
                  sourceGeography={row.original.source.geography}
                />
              ),
            } satisfies ColumnDef<AdminReferenceItem>,
          ]
        : []),
      {
        accessorKey: 'lastUpdatePublicationDate',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Last Update
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <div style={{ minWidth: 130 }}>
            {row.original.lastUpdatePublicationDate
              ? dayjs(row.original.lastUpdatePublicationDate).format(
                  'D MMMM YYYY'
                )
              : '-'}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const ref = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => e.stopPropagation()}
                >
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(ref);
                  }}
                >
                  <Pencil size={12} className="mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        `Delete "${ref.externalId}"? This will also delete ${ref.versionsCount} version(s) and ${ref.updatesCount} update(s). This cannot be undone.`
                      )
                    ) {
                      onDelete(ref);
                    }
                  }}
                  disabled={isDeleting}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 size={12} className="mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [onEdit, onDelete, isDeleting, isStandard]
  );

  const sourceOptions: FilterOption[] = useMemo(
    () => sources.map(s => ({ label: s.title, value: s.id })),
    [sources]
  );

  return (
    <div className="space-y-3">
      <div className="relative w-[280px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
        <Input
          placeholder="Search references..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <FlexRow gap="2" alignItems="center" className="flex-wrap">
        {!isStandard && (
          <OptionFilter
            label="Source"
            options={sourceOptions}
            selected={selectedSources}
            onChange={setSelectedSources}
          />
        )}
        <FilterPopoverTrigger
          label="Geography"
          isActive={selectedGeographies.length > 0}
          operator={
            selectedGeographies.length > 1
              ? 'is any of'
              : selectedGeographies.length === 1
                ? 'is'
                : undefined
          }
          activeContent={
            selectedGeographies.length === 1
              ? getGeographyLabel(selectedGeographies[0]!)
              : selectedGeographies.length > 1
                ? `${selectedGeographies.length} selected`
                : undefined
          }
          onClear={
            selectedGeographies.length > 0
              ? () => setSelectedGeographies([])
              : undefined
          }
        >
          <CountrySelectList
            countries={availableGeographies}
            selected={selectedGeographies}
            onChange={setSelectedGeographies}
          />
        </FilterPopoverTrigger>
        <FlexRow gap="1.5" alignItems="center">
          <span className="text-xs text-neutral-500">Last update</span>
          <Input
            type="date"
            value={lastUpdateFrom}
            onChange={e => setLastUpdateFrom(e.target.value)}
            className="w-[140px] h-8 text-xs"
            placeholder="From"
          />
          <span className="text-xs text-neutral-400">–</span>
          <Input
            type="date"
            value={lastUpdateTo}
            onChange={e => setLastUpdateTo(e.target.value)}
            className="w-[140px] h-8 text-xs"
            placeholder="To"
          />
          {(lastUpdateFrom || lastUpdateTo) && (
            <button
              onClick={() => {
                setLastUpdateFrom('');
                setLastUpdateTo('');
              }}
              className="text-xs text-neutral-500 hover:text-neutral-700"
            >
              Clear
            </button>
          )}
        </FlexRow>
      </FlexRow>

      <div
        className={
          isFetching && !isLoading
            ? 'opacity-50 transition-opacity duration-200'
            : ''
        }
      >
        <DataTable
          columns={columns}
          data={filteredReferences}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={filteredReferences.length}
          options={{
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
            initialState: {
              sorting: [{ id: 'lastUpdatePublicationDate', desc: true }],
            },
          }}
          onRowClick={ref => onEdit(ref)}
          scrollContainerRef={scrollContainerRef}
        />
      </div>
    </div>
  );
};

// ── References subtab ──

const REFERENCE_TAB_VALUES = REFERENCE_TYPE_TABS.map(t => t.value);

export const ReferencesSubtab: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sources } = useAdminSourcesService();
  const { references, isLoading, isFetching } = useAdminReferencesService();
  const { deleteReference, isPending: isDeleting } =
    useAdminDeleteReferenceService();

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [activeTab, handleTabChange] = useUrlParamEnum(
    'tab',
    REFERENCE_TAB_VALUES,
    'regulation'
  );

  const referencesByType = useMemo(() => {
    const grouped: Record<string, AdminReferenceItem[]> = {
      regulation: [],
      guidance: [],
      standard: [],
    };
    for (const r of references) {
      const type = r.type;
      if (type && grouped[type]) {
        grouped[type].push(r);
      }
    }
    return grouped;
  }, [references]);

  const handleEdit = (ref: AdminReferenceItem) => {
    const qs = searchParams.toString();
    navigate(
      `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.references}/${ref.id}${qs ? `?${qs}` : ''}`
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Regulatory References</h2>
          <span className="text-sm text-muted-foreground">
            {references.length} total
          </span>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          Create Reference
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={v => handleTabChange(v as typeof activeTab)}
        className="w-full"
      >
        <TabsList className="w-full">
          {REFERENCE_TYPE_TABS.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex-1">
              {tab.label}
              <Badge variant="secondary" className="ml-2">
                {referencesByType[tab.value]?.length ?? 0}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {REFERENCE_TYPE_TABS.map(tab => (
          <TabsContent key={tab.value} value={tab.value}>
            <SourceTypeTable
              references={referencesByType[tab.value] ?? []}
              sources={sources}
              sourceType={tab.value}
              isLoading={isLoading}
              isFetching={isFetching}
              onEdit={handleEdit}
              onDelete={ref => deleteReference(ref.id)}
              isDeleting={isDeleting}
            />
          </TabsContent>
        ))}
      </Tabs>

      <CreateReferenceModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />
    </div>
  );
};

// ── Update Type Table (reusable per tab) ──

const UpdateTypeTable: FC<{
  updates: AdminUpdateItem[];
  sources: { id: string; title: string; icon: string | null }[];
  sourceType: string;
  isLoading: boolean;
  isFetching: boolean;
  onEdit: (update: AdminUpdateItem) => void;
  onDelete: (update: AdminUpdateItem) => void;
  isDeleting: boolean;
}> = ({
  updates,
  sources,
  sourceType,
  isLoading,
  isFetching,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  const [search, setSearch] = useUrlParam('search');
  const [selectedSources, setSelectedSources] = useUrlParamCsv('source');
  const [selectedTypes, setSelectedTypes] = useUrlParamCsv('updateType');

  // Ref for scroll container
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const restoredRef = useRef(false);

  // Pagination state from URL
  const [pageIndexStr, setPageIndexStr] = useUrlParam('page', '0');
  const [pageSizeStr, setPageSizeStr] = useUrlParam('pageSize', '25');
  const pageIndex = parseInt(pageIndexStr) || 0;
  const pageSize = parseInt(pageSizeStr) || 25;

  const { pagination } = usePagination({
    initialSize: pageSize,
    initialIndex: pageIndex,
    manualPagination: false,
  });

  // Update URL when pagination changes
  useMemo(() => {
    const currentPageIndex = pagination.state.pageIndex.toString();
    const currentPageSize = pagination.state.pageSize.toString();
    if (currentPageIndex !== pageIndexStr) {
      setPageIndexStr(currentPageIndex === '0' ? '' : currentPageIndex);
    }
    if (currentPageSize !== pageSizeStr && currentPageSize !== '25') {
      setPageSizeStr(currentPageSize);
    }
  }, [
    pagination.state.pageIndex,
    pagination.state.pageSize,
    pageIndexStr,
    pageSizeStr,
    setPageIndexStr,
    setPageSizeStr,
  ]);

  // Scroll position restoration
  const scrollSessionKey = `${SCROLL_SESSION_KEY_PREFIX}${sourceType}`;

  // Restore scroll position on mount
  useLayoutEffect(() => {
    if (!scrollContainerRef.current || restoredRef.current || isLoading) {
      return;
    }
    try {
      const savedScroll = sessionStorage.getItem(scrollSessionKey);
      if (savedScroll) {
        const scrollY = parseInt(savedScroll);
        if (!isNaN(scrollY) && scrollY > 0) {
          scrollContainerRef.current.scrollTop = scrollY;
        }
      }
    } catch {
      // Ignore storage errors
    }
    restoredRef.current = true;
  }, [scrollSessionKey, isLoading]);

  // Save scroll position
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let rafId = 0;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        try {
          sessionStorage.setItem(scrollSessionKey, el.scrollTop.toString());
        } catch {
          // Ignore storage errors
        }
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', onScroll);
    };
  }, [scrollSessionKey]);

  const filteredUpdates = useMemo(() => {
    let result = updates;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        u =>
          (u.referenceTitle && u.referenceTitle.toLowerCase().includes(q)) ||
          (u.referenceExternalId &&
            u.referenceExternalId.toLowerCase().includes(q)) ||
          (u.shortSummary && u.shortSummary.toLowerCase().includes(q)) ||
          (u.summary && u.summary.toLowerCase().includes(q))
      );
    }
    if (selectedSources.length > 0) {
      result = result.filter(u => {
        const src = u.sourceId ?? u.referenceSource;
        return src ? selectedSources.includes(src) : false;
      });
    }
    if (selectedTypes.length > 0) {
      result = result.filter(
        u => u.updateType && selectedTypes.includes(u.updateType)
      );
    }
    return result;
  }, [updates, search, selectedSources, selectedTypes]);

  const isStandard = sourceType === 'standard';

  const isNews = sourceType === 'news';

  const columns: ColumnDef<AdminUpdateItem>[] = useMemo(
    () => [
      {
        accessorKey: 'reference',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            {isNews ? 'Title' : 'Reference'}
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <div className="max-w-[500px]">
            <Ellipsis lineClamp={1} hasTooltip>
              {row.original.referenceTitle ?? row.original.shortSummary ?? '-'}
            </Ellipsis>
          </div>
        ),
      },
      {
        accessorKey: 'shortSummary',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Short Summary
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <div className="max-w-[520px]">
            <Ellipsis lineClamp={1} hasTooltip>
              {row.original.shortSummary ?? row.original.summary ?? '-'}
            </Ellipsis>
          </div>
        ),
      },
      ...(!isStandard && !isNews
        ? [
            {
              accessorKey: 'source',
              header: 'Source',
              cell: ({ row }: { row: { original: AdminUpdateItem } }) => {
                const srcId =
                  row.original.sourceId ?? row.original.referenceSource;
                const source = sources.find(s => s.id === srcId);
                const sourceIcon = row.original.sourceIcon;
                const sourceGeography = row.original.sourceGeography;
                return (
                  <SourceBadge
                    sourceTitle={source?.title ?? srcId ?? ''}
                    sourceIcon={
                      sourceIcon
                        ? getSourceIconUrl(srcId ?? '', sourceIcon)
                        : undefined
                    }
                    sourceGeography={sourceGeography}
                  />
                );
              },
            } satisfies ColumnDef<AdminUpdateItem>,
          ]
        : []),
      ...(isNews
        ? [
            {
              accessorKey: 'sourceTitle',
              header: 'Source',
              cell: ({ row }: { row: { original: AdminUpdateItem } }) => {
                const sourceIcon = row.original.sourceIcon;
                const sourceId = row.original.sourceId;
                const sourceTitle = row.original.sourceTitle;
                const sourceGeography = row.original.sourceGeography;
                if (!sourceTitle) return <span className="text-sm">-</span>;
                return (
                  <SourceBadge
                    sourceTitle={sourceTitle}
                    sourceIcon={
                      sourceIcon && sourceId
                        ? getSourceIconUrl(sourceId, sourceIcon)
                        : undefined
                    }
                    sourceGeography={sourceGeography}
                  />
                );
              },
            } satisfies ColumnDef<AdminUpdateItem>,
            {
              accessorKey: 'articleUrl',
              header: 'Article',
              cell: ({ row }: { row: { original: AdminUpdateItem } }) =>
                row.original.articleUrl ? (
                  <a
                    href={row.original.articleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline truncate block max-w-[300px]"
                    onClick={e => e.stopPropagation()}
                  >
                    Link
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                ),
            } satisfies ColumnDef<AdminUpdateItem>,
          ]
        : []),
      ...(!isNews
        ? [
            {
              accessorKey: 'updateType',
              header: ({ column }: { column: any }) => (
                <TanstackSortableColumnHeader column={column}>
                  Update Type
                </TanstackSortableColumnHeader>
              ),
              cell: ({ row }: { row: { original: AdminUpdateItem } }) =>
                row.original.updateType ? (
                  <UpdateTypeBadge updateType={row.original.updateType} />
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                ),
            } satisfies ColumnDef<AdminUpdateItem>,
          ]
        : []),
      {
        accessorKey: 'publicationDate',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Publication Date
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <div style={{ minWidth: 130 }}>
            {dayjs(row.original.publicationDate.substring(0, 10)).format(
              'D MMMM YYYY'
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const u = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => e.stopPropagation()}
                >
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(u);
                  }}
                >
                  <Pencil size={12} className="mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        'Delete this update? This cannot be undone.'
                      )
                    ) {
                      onDelete(u);
                    }
                  }}
                  disabled={isDeleting}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 size={12} className="mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [onEdit, onDelete, isDeleting, isStandard, sources]
  );

  const sourceOptions: FilterOption[] = useMemo(
    () => sources.map(s => ({ label: s.title, value: s.id })),
    [sources]
  );

  return (
    <div className="space-y-3">
      <div className="relative w-[280px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
        <Input
          placeholder="Search updates"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      {!isStandard && (
        <FlexRow gap="2" alignItems="center" className="flex-wrap">
          <OptionFilter
            label="Source"
            options={sourceOptions}
            selected={selectedSources}
            onChange={setSelectedSources}
          />
          <OptionFilter
            label="Type"
            options={UPDATE_TYPE_OPTIONS}
            selected={selectedTypes}
            onChange={setSelectedTypes}
          />
        </FlexRow>
      )}
      {isStandard && (
        <FlexRow gap="2" alignItems="center" className="flex-wrap">
          <OptionFilter
            label="Type"
            options={UPDATE_TYPE_OPTIONS}
            selected={selectedTypes}
            onChange={setSelectedTypes}
          />
        </FlexRow>
      )}

      <div
        className={
          isFetching && !isLoading
            ? 'opacity-50 transition-opacity duration-200'
            : ''
        }
      >
        <DataTable
          columns={columns}
          data={filteredUpdates}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={filteredUpdates.length}
          options={{
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
            initialState: {
              sorting: [{ id: 'publicationDate', desc: true }],
            },
          }}
          onRowClick={u => onEdit(u)}
          scrollContainerRef={scrollContainerRef}
        />
      </div>
    </div>
  );
};

// ── Updates subtab ──

const UPDATE_TYPE_TABS = [
  { value: 'regulation', label: 'Regulations' },
  { value: 'guidance', label: 'Guidances' },
  { value: 'standard', label: 'Standards' },
  { value: 'news', label: 'News' },
] as const;

const UPDATE_TAB_VALUES_ARR = [
  'regulation',
  'guidance',
  'standard',
  'news',
] as const;

export const UpdatesSubtab: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sources } = useAdminSourcesService();
  const { references } = useAdminReferencesService();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const {
    updates: allUpdates,
    isLoading,
    isFetching,
  } = useAdminUpdatesService({});
  const { deleteUpdate, isPending: isDeleting } = useAdminDeleteUpdateService();

  const [activeTab, handleTabChange] = useUrlParamEnum(
    'tab',
    UPDATE_TAB_VALUES_ARR,
    'regulation'
  );

  const handleEdit = (update: AdminUpdateItem) => {
    const qs = searchParams.toString();
    navigate(
      `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.updates}/${update.id}${qs ? `?${qs}` : ''}`
    );
  };

  // Build a map from reference ID to its type for grouping updates
  const referenceTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of references ?? []) {
      if (r.type) map[r.id] = r.type;
    }
    return map;
  }, [references]);

  const updatesByType = useMemo(() => {
    const grouped: Record<string, AdminUpdateItem[]> = {
      regulation: [],
      guidance: [],
      standard: [],
      news: [],
    };
    for (const u of allUpdates) {
      if (u.kind === 'news') {
        grouped.news.push(u);
      } else {
        const refId = u.referenceId;
        const type = refId ? referenceTypeMap[refId] : undefined;
        if (type && grouped[type]) {
          grouped[type].push(u);
        }
      }
    }
    return grouped;
  }, [allUpdates, referenceTypeMap]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Updates</h2>
          <span className="text-sm text-muted-foreground">
            {allUpdates.length} total
          </span>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>Create Update</Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={v => handleTabChange(v as typeof activeTab)}
        className="w-full"
      >
        <TabsList className="w-full">
          {UPDATE_TYPE_TABS.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex-1">
              {tab.label}
              <Badge variant="secondary" className="ml-2">
                {updatesByType[tab.value]?.length ?? 0}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {UPDATE_TYPE_TABS.map(tab => (
          <TabsContent key={tab.value} value={tab.value}>
            <UpdateTypeTable
              updates={updatesByType[tab.value] ?? []}
              sources={sources}
              sourceType={tab.value}
              isLoading={isLoading}
              isFetching={isFetching}
              onEdit={handleEdit}
              onDelete={u => deleteUpdate(u.id)}
              isDeleting={isDeleting}
            />
          </TabsContent>
        ))}
      </Tabs>

      <CreateUpdateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />
    </div>
  );
};
